const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const cleanMessage = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
};

const extractMessage = (value, seen = new Set()) => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') {
    return cleanMessage(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (seen.has(value)) return '';
  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .map((item) => extractMessage(item, seen))
      .filter(Boolean)
      .join('\n');
  }

  if (typeof value === 'object') {
    const directMessage =
      value.message ||
      value.msg ||
      value.error ||
      value.errors ||
      value.data ||
      value.details;

    if (directMessage && directMessage !== value) {
      const message = extractMessage(directMessage, seen);
      if (message) return message;
    }

    return Object.values(value)
      .map((item) => extractMessage(item, seen))
      .filter(Boolean)
      .join('\n');
  }

  return '';
};

export const getApiErrorMessage = (
  error,
  fallback = DEFAULT_ERROR_MESSAGE,
) => {
  const responseData = error?.response?.data;
  const message =
    extractMessage(responseData?.data) ||
    extractMessage(responseData?.message) ||
    extractMessage(responseData?.error) ||
    extractMessage(responseData?.errors) ||
    extractMessage(responseData) ||
    extractMessage(error?.message);

  return message || fallback;
};

export const getApiErrorMessageAsync = async (
  error,
  fallback = DEFAULT_ERROR_MESSAGE,
) => {
  const responseData = error?.response?.data;

  if (typeof Blob !== 'undefined' && responseData instanceof Blob) {
    const text = await responseData.text();

    try {
      return (
        getApiErrorMessage(
          { response: { data: JSON.parse(text) } },
          fallback,
        ) || fallback
      );
    } catch (parseError) {
      return cleanMessage(text) || fallback;
    }
  }

  return getApiErrorMessage(error, fallback);
};

export const getToastId = (status, message) =>
  `${status}:${cleanMessage(message)}`.slice(0, 180);

export const markApiToastShown = (error) => {
  if (error && typeof error === 'object') {
    error.__apiToastShown = true;
  }
};

export const notifyApiError = (
  error,
  toast,
  fallback = DEFAULT_ERROR_MESSAGE,
) => {
  const message = getApiErrorMessage(error, fallback);
  const toastId = getToastId('error', message);

  if (error?.__apiToastShown) {
    return message;
  }

  if (!toast?.isActive?.(toastId)) {
    toast({
      id: toastId,
      title: message,
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
  }

  markApiToastShown(error);
  return message;
};

export const notifyApiErrorAsync = async (
  error,
  toast,
  fallback = DEFAULT_ERROR_MESSAGE,
) => {
  const message = await getApiErrorMessageAsync(error, fallback);
  const toastId = getToastId('error', message);

  if (error?.__apiToastShown) {
    return message;
  }

  if (!toast?.isActive?.(toastId)) {
    toast({
      id: toastId,
      title: message,
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
  }

  markApiToastShown(error);
  return message;
};
